import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useMutation } from 'react-apollo';

import ImageUpload from '../../../ImageUpload';
import { getErrorString } from '../../../../../util/normalize-errors';
import { assetImagesUpload as assetImagesUploadMutation } from '../../../../../graphql/mutations/account';
import Alert from '../../../Alert';
import { IMAGE_SIZE_CODE } from '../../../../../constants/image-size-codes';
import { photoUploadFilter } from '../../../../../util/photo-upload';

const propTypes = {
  onSelect: PropTypes.func.isRequired,
  selectedPhoto: PropTypes.shape({
    id: PropTypes.string.isRequired,
    thumbnail_urls: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }),
};

const CustomPhotoUploadTab = ({ onSelect, selectedPhoto }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assetImagesUpload] = useMutation(assetImagesUploadMutation);

  const onAssetUpdate = async event => {
    if (loading || !event.target.files.length) {
      return;
    }

    setError(null);

    const [file] = event.target.files;

    if (!photoUploadFilter(file)) {
      setError('Please provide a JPEG, HEIC or PNG image.');
      return;
    }

    try {
      setLoading(true);

      const imageUploadResponse = await assetImagesUpload({
        variables: {
          input: {
            images: [{ filename: file.name, content_type: file.type }],
          },
        },
      });

      const [
        imageUploadData,
      ] = imageUploadResponse.data.assetImagesUpload.image_uploads;

      await axios.put(imageUploadData.url, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      const imagePreviewUrl = URL.createObjectURL(file);

      // Create and select a faux-Photo object so that we maintain a unified API
      // across existing Photo selections vs custom uploads.
      onSelect({
        id: imageUploadData.id,
        // Assign the URLs to all photo size codes
        // to be safe as some templates render different photo sizes
        // (i.e. Waldograms could show LARGE vs Collages that show MEDIUM).
        thumbnail_urls: Object.values(IMAGE_SIZE_CODE).map(size => ({
          size_code: size,
          url: imagePreviewUrl,
          width: 0,
          height: 0,
        })),
        // Use this flag so that the configurator knows to
        // assign this photo as a custom upload in the configuration mutation.
        custom: true,
      });
    } catch (uploadError) {
      setError(getErrorString(uploadError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      {error && <Alert type="danger">{error}</Alert>}

      <ImageUpload
        id="custom-print-photo-upload"
        className="custom-print-photo-upload"
        onImageDismiss={() => {
          onSelect(null);
        }}
        onInputChange={event => onAssetUpdate(event)}
        buttonText="Upload a photo"
        loading={loading}
        image={selectedPhoto}
      />
      {selectedPhoto && (
        <p className="my-2 text-italic text-center text-sm">
          Click &ldquo;Done&rdquo; below to apply the photo to the template.
        </p>
      )}
    </Fragment>
  );
};

CustomPhotoUploadTab.propTypes = propTypes;

export default CustomPhotoUploadTab;
